import axios from "axios";

import { API_URL } from "../../constants";
import { authHeader, clearFunnelToken, listingAuthHeader, setFunnelToken } from "../../utils/auth";
import { captureIntakeFailure } from "../../utils/intakeLimits";
import {
  CANCEL_LISTING,
  CANCEL_LISTING_FAIL,
  CANCEL_LISTING_SUCCESS,
  CLEAR_ACTIVE_LISTING_ID,
  CONFIRM_LISTING,
  CONFIRM_LISTING_FAIL,
  CONFIRM_LISTING_SUCCESS,
  CREATE_MANUAL_LISTING,
  CREATE_MANUAL_LISTING_FAIL,
  CREATE_MANUAL_LISTING_SUCCESS,
  DELETE_IMAGE,
  DELETE_IMAGE_FAIL,
  DELETE_IMAGE_SUCCESS,
  DELETE_INPUT_DOCUMENT,
  DELETE_INPUT_DOCUMENT_FAIL,
  DELETE_INPUT_DOCUMENT_SUCCESS,
  FETCH_LISTING,
  FETCH_LISTING_FAIL,
  FETCH_LISTING_SUCCESS,
  SET_ACTIVE_LISTING_ID,
  UPLOAD_IMAGES,
  UPLOAD_IMAGES_FAIL,
  UPLOAD_IMAGES_SUCCESS,
  UPLOAD_INPUT_DOCUMENT,
  UPLOAD_INPUT_DOCUMENT_FAIL,
  UPLOAD_INPUT_DOCUMENT_SUCCESS,
} from "../actionTypes";

/**
 * @typedef {import('redux').Dispatch} Dispatch
 */

/**
 * Shared shape for the listing API thunks: dispatch START, run one axios
 * request, then dispatch SUCCESS with the response data or FAIL with a
 * normalized error payload (and a console.error for debugging). Collapses six
 * near-identical thunks into one place so the dispatch/error contract can't drift.
 *
 * @param {{ start: string, success: string, fail: string, label: string, intake?: boolean, onSuccess?: (data: any) => void }} types
 * @param {() => Promise<{ data: any }>} request
 * @returns {(dispatch: Dispatch) => Promise<{ok: boolean, data?: any, error?: any, status?: number | null}>}
 */
const apiThunk =
  ({ start, success, fail, label, onSuccess, intake = false }, request) =>
  (dispatch) => {
    dispatch({ type: start });
    return request()
      .then((res) => {
        if (onSuccess) onSuccess(res.data);
        dispatch({ type: success, payload: res.data });
        return { ok: true, data: res.data };
      })
      .catch((err) => {
        const error =
          (intake ? captureIntakeFailure(err) : null) || err.response?.data || err.message;
        const status = err.response?.status ?? null;
        console.error(`${label}:`, error);
        dispatch({ type: fail, payload: { error, status } });
        return { ok: false, error, status };
      });
  };

export { submitListing } from "./zillowImport";

/**
 * @param {string} listingId
 * @returns {(dispatch: Dispatch) => Promise<{ok: boolean, data?: any, error?: any, status?: number | null}>}
 */
export const fetchListing = (listingId) =>
  apiThunk(
    {
      start: FETCH_LISTING,
      success: FETCH_LISTING_SUCCESS,
      fail: FETCH_LISTING_FAIL,
      label: "Listing fetch failed",
    },
    () =>
      axios.get(`${API_URL}/listings/${listingId}/`, {
        headers: listingAuthHeader(listingId),
        timeout: 15000,
      })
  );

/**
 * @param {string} listingId
 * @param {string} paymentMethodId
 * @returns {(dispatch: Dispatch) => void}
 */
export const confirmListing = (listingId, paymentMethodId) =>
  apiThunk(
    {
      start: CONFIRM_LISTING,
      success: CONFIRM_LISTING_SUCCESS,
      fail: CONFIRM_LISTING_FAIL,
      label: "Listing confirmation failed",
    },
    () =>
      axios.post(
        `${API_URL}/listings/${listingId}/confirm/`,
        { payment_method_id: paymentMethodId },
        { headers: listingAuthHeader(listingId) }
      )
  );

/**
 * @param {string} listingId
 * @returns {(dispatch: Dispatch) => Promise<{ok: boolean, data?: any, error?: any, status?: number | null}>}
 */
export const cancelListing = (listingId) =>
  apiThunk(
    {
      start: CANCEL_LISTING,
      success: CANCEL_LISTING_SUCCESS,
      fail: CANCEL_LISTING_FAIL,
      label: "Listing cancellation failed",
      onSuccess: () => clearFunnelToken(listingId),
    },
    () =>
      axios.post(
        `${API_URL}/listings/${listingId}/cancel/`,
        {},
        { headers: listingAuthHeader(listingId) }
      )
  );

/**
 * Create a manual listing with property details
 * @param {Object} propertyData
 * @returns {(dispatch: Dispatch) => void}
 */
export const createManualListing = (propertyData) =>
  apiThunk(
    {
      start: CREATE_MANUAL_LISTING,
      success: CREATE_MANUAL_LISTING_SUCCESS,
      fail: CREATE_MANUAL_LISTING_FAIL,
      label: "Manual listing creation failed",
      intake: true,
      onSuccess: (data) => {
        if (data.uuid && data.funnel_token) setFunnelToken(data.uuid, data.funnel_token);
      },
    },
    () => axios.post(`${API_URL}/listings/create-manual/`, propertyData, { headers: authHeader() })
  );

/**
 * Upload images for a listing
 * @param {string} listingUuid
 * @param {File[]} files
 * @param {(progress: number) => void} [onProgress]
 * @returns {(dispatch: Dispatch) => void}
 */
export const uploadImages = (listingUuid, files, onProgress) => {
  const formData = new FormData();
  for (const file of files) {
    formData.append("images", file);
  }
  return apiThunk(
    {
      start: UPLOAD_IMAGES,
      success: UPLOAD_IMAGES_SUCCESS,
      fail: UPLOAD_IMAGES_FAIL,
      label: "Image upload failed",
      intake: true,
    },
    () =>
      axios.post(`${API_URL}/listings/${listingUuid}/upload-images/`, formData, {
        headers: { ...listingAuthHeader(listingUuid), "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      })
  );
};

/**
 * Set the UUID of the listing the user is actively working with.
 *
 * Most of the time the reducer picks this up automatically from
 * *_SUCCESS payloads — this explicit action is for cases where the
 * container already has the ID in hand (e.g. hydrating from
 * sessionStorage at app boot, or retrieving it from a page param).
 * @param {string} listingUuid
 * @returns {{ type: string; payload: string }}
 */
export const setActiveListingId = (listingUuid) => ({
  type: SET_ACTIVE_LISTING_ID,
  payload: listingUuid,
});

/**
 * Clear the active listing UUID.
 *
 * Used when the user navigates away ("Not your property?" link, Go
 * Back button) or when the listing is no longer valid.
 * @returns {{ type: string }}
 */
export const clearActiveListingId = () => ({ type: CLEAR_ACTIVE_LISTING_ID });

/**
 * Delete an image from a listing
 * @param {string} listingUuid
 * @param {number|string} imageId
 * @returns {(dispatch: Dispatch) => void}
 */
export const deleteImage = (listingUuid, imageId) =>
  apiThunk(
    {
      start: DELETE_IMAGE,
      success: DELETE_IMAGE_SUCCESS,
      fail: DELETE_IMAGE_FAIL,
      label: "Image deletion failed",
    },
    () =>
      axios.delete(`${API_URL}/listings/${listingUuid}/delete-image/${imageId}/`, {
        headers: listingAuthHeader(listingUuid),
      })
  );

/**
 * Upload a PDF whose CONTENTS the analysis should read.
 * @param {string} listingUuid
 * @param {File} file
 */
export const uploadInputDocument = (listingUuid, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return apiThunk(
    {
      start: UPLOAD_INPUT_DOCUMENT,
      success: UPLOAD_INPUT_DOCUMENT_SUCCESS,
      fail: UPLOAD_INPUT_DOCUMENT_FAIL,
      label: "Document upload failed",
      intake: true,
    },
    () =>
      axios.post(`${API_URL}/listings/${listingUuid}/input-documents/`, formData, {
        headers: { ...listingAuthHeader(listingUuid), "Content-Type": "multipart/form-data" },
      })
  );
};

/**
 * @param {string} listingUuid
 * @param {string} documentId
 */
export const deleteInputDocument = (listingUuid, documentId) =>
  apiThunk(
    {
      start: DELETE_INPUT_DOCUMENT,
      success: DELETE_INPUT_DOCUMENT_SUCCESS,
      fail: DELETE_INPUT_DOCUMENT_FAIL,
      label: "Document removal failed",
    },
    () =>
      axios.delete(`${API_URL}/listings/${listingUuid}/input-documents/`, {
        headers: listingAuthHeader(listingUuid),
        params: { id: documentId },
      })
  );
